export interface MergeBatchGroup<Group, Pair> {
    group: Group;
    pairs: Pair[];
}

export interface MergeBatchGroupResult<Group, Pair> {
    group: Group;
    completedPairs: Pair[];
    failure: { cause: unknown } | null;
    stopped: boolean;
}

interface MergeBatchCallbacks<Group, Pair> {
    isStopRequested: () => boolean;
    onGroupStarted: (group: Group) => void;
    mergePair: (pair: Pair, group: Group) => Promise<void>;
    onGroupResult: (result: MergeBatchGroupResult<Group, Pair>) => Promise<void>;
}

export const runMergeBatch = async <Group, Pair>(
    groups: MergeBatchGroup<Group, Pair>[],
    callbacks: MergeBatchCallbacks<Group, Pair>
): Promise<{ stopped: boolean }> => {
    let stopped = false;

    for (const { group, pairs } of groups) {
        if (callbacks.isStopRequested()) {
            stopped = true;
            break;
        }
        if (pairs.length === 0)
            throw new Error('A merge group has no account pairs');

        const completedPairs: Pair[] = [];
        let attempted = 0;
        let failure: { cause: unknown } | null = null;
        let groupStopped = false;

        for (const pair of pairs) {
            if (callbacks.isStopRequested()) {
                stopped = true;
                groupStopped = true;
                break;
            }
            if (attempted === 0) callbacks.onGroupStarted(group);
            attempted += 1;
            try {
                await callbacks.mergePair(pair, group);
                completedPairs.push(pair);
            } catch (error) {
                failure = { cause: error };
                break;
            }
        }

        if (attempted === 0) break;
        if (callbacks.isStopRequested() && attempted < pairs.length) {
            stopped = true;
            groupStopped = true;
        }
        await callbacks.onGroupResult({
            group,
            completedPairs,
            failure,
            stopped: groupStopped,
        });
    }

    return { stopped };
};

export interface MergeProgress {
    total: number;
    processed: number;
    succeeded: number;
    partial: number;
    failed: number;
    historyErrors: number;
    startedAt: number;
    lastProcessedAt?: number;
    finishedAt?: number;
    stopped: boolean;
    fatalError?: string;
}

export const formatMergeDuration = (milliseconds: number): string => {
    const seconds = Math.max(0, Math.floor(milliseconds / 1000));
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainder = seconds % 60;
    const pad = (value: number) => String(value).padStart(2, '0');
    return hours > 0
        ? `${hours}:${pad(minutes)}:${pad(remainder)}`
        : `${pad(minutes)}:${pad(remainder)}`;
};

export const estimateMergeRemaining = (
    progress: MergeProgress,
    now: number
): number | null => {
    if (
        progress.processed === 0 ||
        progress.processed >= progress.total ||
        progress.lastProcessedAt === undefined ||
        progress.finishedAt !== undefined
    )
        return null;

    const average =
        (progress.lastProcessedAt - progress.startedAt) / progress.processed;
    const elapsedSinceLastGroup = Math.max(0, now - progress.lastProcessedAt);
    const remaining =
        average * (progress.total - progress.processed) - elapsedSinceLastGroup;
    return Number.isFinite(remaining) && remaining > 0 ? remaining : null;
};
